import { Injectable, EventEmitter } from '@angular/core';
import { AppConfig } from '../model/config';
import { HttpHelper } from './http.service';
import { Availability } from '../model/availability';
import * as moment from 'moment';
import { WorkShiftGroup } from '../model/workShiftGroup';
import { GroupType } from '../common/enums';
import { WorkShift } from '../model/workShift';
import { SignalRService } from './signalr.service';

import { SettingsService } from './settings.service';
import { NewDateTrigger } from '../helpers/newdate-trigger';
import { BehaviorSubject } from 'rxjs';
import _ from 'lodash';


@Injectable()
export class WorkShiftService {
  private internalWorkShiftGroups: WorkShiftGroup[] = [];
  public workShiftGroupsUpdated$: EventEmitter<boolean>;
  private newDateEvent: NewDateTrigger;

  private readonly windowStart: string = this.settings.windowStart.toISOString();
  private readonly windowEnd: string = this.settings.windowEnd.toISOString();

  public get workShiftGroups(): WorkShiftGroup[] {
    return _.cloneDeep(this.internalWorkShiftGroups);
  }

  public get availabilities(): Availability[] {
    return this.getAvailabilitiesFromWorkShiftGroups(this.internalWorkShiftGroups);
  }

  constructor(
    private readonly configuration: AppConfig,
    private readonly httpClient: HttpHelper,
    private readonly signalRService: SignalRService,
    private readonly settings: SettingsService) {

    this.workShiftGroupsUpdated$ = new EventEmitter<boolean>();
    this.signalRService.updateWorkShiftGroups$.subscribe((groups: WorkShiftGroup[]) => { this.onUpdateWorkShiftGroups(groups); });

    this.newDateEvent = new NewDateTrigger(moment(this.windowEnd).toDate())
    this.newDateEvent.on('new-date', () => {
      this.readWorkShifts(moment(this.windowEnd).toDate());
    });
  }

  public async initService() {
    this.internalWorkShiftGroups = await this.getWorkShiftGroups('ARN', moment(this.windowStart).format("YYYYMMDD"), moment(this.windowEnd).format("YYYYMMDD"));
    this.onUpdateWorkShiftGroups(this.internalWorkShiftGroups);
  }

  public async readWorkShifts(date: Date) {
    let workShiftGroups = await this.getWorkShiftGroups('ARN', moment(date).format("YYYYMMDD"), moment(date).format("YYYYMMDD"));
    this.onUpdateWorkShiftGroups(workShiftGroups);
  }

  public findWorkShiftForAir(userMappingId: string) {

    let airWorkShifts = this.getWorkShiftsForAir();

    let workShiftForUser: WorkShift = undefined;

    let workShiftsForUser = airWorkShifts.filter(w => w.userMappingID === userMappingId)
    if (workShiftsForUser.length > 1) {
      console.log("Should not happend. Only one workshift/user/type");
    }
    if (workShiftsForUser.length === 1) {
      workShiftForUser = workShiftsForUser[0];
    }

    return workShiftForUser;
  }

  public getWorkShiftsForAir(): WorkShift[] {
    let workShiftGroups = this.internalWorkShiftGroups.filter(a => a.type === GroupType.Air || a.type === GroupType.Standby);

    if (workShiftGroups.length === 1) { return workShiftGroups[0].workShifts; }

    if (workShiftGroups.length > 1) {
      let workshifts = [];
      workShiftGroups.forEach(wsg => {
        workshifts = workshifts.concat(wsg.workShifts);
      });

      return workshifts;
    }

    console.log("No workShiftGroups found!");

  }

  public getWorkShiftForUserMappingId(userMappingId: string): WorkShift {
    let workShiftsForAir = this.getWorkShiftsForAir();

    let workShifts = workShiftsForAir.filter(w => w.userMappingID == userMappingId);

    if (workShifts.length == 1) return workShifts[0];

    if (workShifts.length > 1) throw new Error('Multiple workshifts found for usermapping (' + userMappingId + ')');

    return undefined;


  }

  public findAvailabilityForUserMapping(userMappingId: string, date: Date): Availability {

    let workShiftsForUser = this.findWorkShiftForAir(userMappingId);
    let matchingAvailabilities = workShiftsForUser.availabilities.filter(a => a.start <= date && a.end >= date);

    if (matchingAvailabilities.length === 1) return matchingAvailabilities[0];

    if (matchingAvailabilities.length > 1) {
      console.log("Found multiple availabilities for date. Overlapp should not occur");
      return undefined;
    }

    console.log("No matching availability found!");
    return undefined;

  }

  public findAvailability(id: string): Availability {
    return this.availabilities.find(a => a.id === id);
  }

  public getAvailabilitiesForWorkShiftGroups(workShiftGroups: WorkShiftGroup[]) {
    return this.getAvailabilitiesFromWorkShiftGroups(workShiftGroups);
  }

  private removeWorkShiftsOutsideOfWindow(windowStart: Date) {

    this.internalWorkShiftGroups.forEach((wg) => {

      wg.workShifts.forEach((ws) => {
        this.removeAvailabilitiesOutsideOfWindowFromWorkShift(ws, windowStart);
      });

      this.removeWorkShiftsWithoutAvailabilitiesFromGroup(wg);

    });



  }

  private removeAvailabilitiesOutsideOfWindowFromWorkShift(ws: WorkShift, windowStart: Date) {
    let availabilitiesOutsideWindow = ws.availabilities.filter((a) => a.end < windowStart);

    if (availabilitiesOutsideWindow.length === 0) return;

    availabilitiesOutsideWindow.forEach((a) => {
      let index = ws.availabilities.findIndex((av) => av.id === a.id);
      ws.availabilities.splice(index, 1);
    });

  }

  private removeWorkShiftsWithoutAvailabilitiesFromGroup(wg: WorkShiftGroup) {
    let workshiftWithoutAvailabilities = wg.workShifts.filter((ws) => ws.availabilities.length === 0);

    workshiftWithoutAvailabilities.forEach((ws) => {
      let index = wg.workShifts.findIndex(wgws => wgws.userMappingID === ws.userMappingID);
      wg.workShifts.splice(index, 1);
    });
  }

  private onUpdateWorkShiftGroups(groups: WorkShiftGroup[]) {
    if (this.internalWorkShiftGroups.length == 0) return;
    groups.forEach((updatedGroup) => {
      let existingGroup = this.internalWorkShiftGroups.find((g) => g.id == updatedGroup.id);

      if (updatedGroup.dateKey !== null && updatedGroup.dateKey !== undefined) {
        // remove all availabilities with same datekey as current group -
        existingGroup.workShifts.forEach((ws) => {
          ws.availabilities = ws.availabilities.filter(a => a.dateKey !== updatedGroup.dateKey);
        });
      }

      updatedGroup.workShifts.forEach((updatedWorkShift) => {
        let existingWorkShiftIndex = existingGroup.workShifts.findIndex((g) => g.userMappingID === updatedWorkShift.userMappingID);

        this.removeAvailabilityIfChangedBetweenGroups(existingGroup, updatedWorkShift.availabilities)
        if (existingWorkShiftIndex === -1) {
          existingGroup.workShifts.push(updatedWorkShift);
          return;
        }

        this.addOrUpdateAvailability(updatedWorkShift, existingGroup, existingWorkShiftIndex)

      });

      existingGroup.workShifts.sort((a, b) => a.displayOrder - b.displayOrder);

    });

    this.convertAvailabilityDates(this.internalWorkShiftGroups);
    this.removeWorkShiftsOutsideOfWindow(this.settings.windowStart);

    this.workShiftGroupsUpdated$.emit(true);
  }

  private addOrUpdateAvailability(updatedWorkShift: WorkShift, existingGroup: WorkShiftGroup, existingWorkShiftIndex: number) {

    let existingWorkShift = new WorkShift(existingGroup.workShifts[existingWorkShiftIndex]);
    existingWorkShift.displayOrder = updatedWorkShift.displayOrder;
    existingWorkShift.availabilities = existingWorkShift.availabilities.filter(a => a.dateKey !== updatedWorkShift.dateKey);

    updatedWorkShift.availabilities.forEach((updatedAvailability) => {

      let existingAvailabilityIndex = existingWorkShift.availabilities.findIndex(a => a.id === updatedAvailability.id);

      if (existingAvailabilityIndex === -1) {
        existingWorkShift.availabilities.push(Availability.toTypeSafe(updatedAvailability));
        existingGroup.workShifts[existingWorkShiftIndex] = existingWorkShift;
        return;
      }

      existingWorkShift.availabilities[existingAvailabilityIndex] = Availability.toTypeSafe(updatedAvailability);

      existingWorkShift.busNumbers = existingWorkShift.getBusNumbers();
      existingGroup.workShifts[existingWorkShiftIndex] = existingWorkShift;

    });
  }

  private removeAvailabilityIfChangedBetweenGroups(workShiftGroup: WorkShiftGroup, updatedAvailabilities: Availability[]) {

    // Removes availabilities with the same ID as updated availabilties from other workshift groups
    let workshiftGroupsToSearch = this.internalWorkShiftGroups.filter(w => w.id !== workShiftGroup.id);
    workshiftGroupsToSearch.forEach(wg => {

      wg.workShifts.forEach(ws => {

        let filtered = ws.availabilities.filter(
          function (e: Availability) {
            return this.indexOf(e.id) < 0;
          },
          updatedAvailabilities.map(a => a.id)
        );
        ws.availabilities = filtered;
      })

    });
  }

  private getAvailabilitiesFromWorkShiftGroups(groups: WorkShiftGroup[]): Availability[] {

    let availabilities = [];
    groups.forEach(group => {
      group.workShifts.forEach(workShift => {
        workShift.availabilities.forEach(child => {
          availabilities.push(new Availability(child));
        })
      })
    });
    return availabilities;
  }

  private convertAvailabilityDates(groups: WorkShiftGroup[]) {
    groups.forEach(group => {
      let workShifts = [];

      group.workShifts.forEach(workShift => {
        let myConvertedWorkShift = new WorkShift(workShift);
        let availabilities = [];
        myConvertedWorkShift.availabilities.forEach(child => {
          availabilities.push(Availability.toTypeSafe(child));
        })
        myConvertedWorkShift.availabilities = availabilities;
        workShifts.push(myConvertedWorkShift);
      })
      group.workShifts = workShifts;
    });
  }

  // Availabilities
  public async getAvailabilityForReports(airportId: string, date: Date): Promise<string[]> {
    let url: string = this.configuration.backendUrl + "/api/availability/drivers/" + airportId + "/" + moment(date).format("YYYYMMDD");

    return await this.httpClient.get(url, () => { },
      () => { }).toPromise<string[]>();
  }

  public async setWorkShiftGroups(groups: WorkShiftGroup[]) {

    return this.httpClient.post(this.configuration.backendUrl + "/api/availability/" + this.windowStart,
      groups,
      (result) => { },
      (exception) => { }).toPromise<any>();
  }

  private async getWorkShiftGroups(airportId: string, windowStart: string, windowEnd: string) {
    let url: string = this.configuration.backendUrl + "/api/availability/workShift/" + airportId + "/" + windowStart + "/" + windowEnd;

    return await this.httpClient.get(url, () => { },
      () => { }).toPromise<any>();
  }
}
